import { useState } from 'react';
import { Activity, BarChart3, Clock, Users, ArrowRight, Info, ChevronDown, ChevronUp, Sun, Sunset } from 'lucide-react';
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import MetricCard from '../../components/projects/MetricCard';
import RACStratigraphy from '../../components/charts/RACStratigraphy';
import BankAnalysis from '../../components/project/BankAnalysis';
import CapacityRegressionPanel from '../../components/CapacityRegressionPanel';
import { useProject } from './ProjectContext';
import { useProjectAnalysis } from './useProjectAnalysis';

const ApcasPage = () => {
  const { project } = useProject();
  // chartData/nopHourly/capacidadUtilAdj feed the capacity-loss stratigraphy,
  // moved here from the Overview (it is analysis, not the As-Is photo).
  const { apcas, safeNum, chartData, nopHourly, capacidadUtilAdj, iataBusyDayStr, bankData, bankChartData } = useProjectAnalysis();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { language } = useLanguage();
  const [showApcasDetails, setShowApcasDetails] = useState(false);

  const cardBg = 'bg-[var(--surface)] border-[var(--border)]';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';

  // Apron aircraft mix, ICAO ARC (Annex 14 — wingspan / outer main gear span).
  // This is the PLATFORM taxonomy: it sizes stands and taxiways, and an A320 is C.
  // The runway node uses RECAT-EU instead (same letters, wake turbulence, A320 = D)
  // and lives on the RunCap page. Never read one where the other is expected.
  const icaoArcMix =
    ((project as any)?.analysis_results?.capacity_integration?.icao_arc_mix as
      | Record<string, number>
      | undefined) ?? null;

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ROW 1: KEY METRICS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard title="RAC (Apron Capacity)" value={`${safeNum(apcas.rac_capacity, 1)} mov/h`} sub={`CMP Infra: ${safeNum(apcas.cmp_infra, 0)} mov/h`} icon={Activity} isDark={isDark} color="blue" description={language === 'es' ? 'Capacidad real del apron tras ajustes' : 'Real apron capacity after adjustments'} />
        <MetricCard title="BCI (Balance Index)" value={safeNum(apcas.bci_index, 3)} sub={apcas.bci_index < 0.7 ? (language === 'es' ? 'Desequilibrado' : 'Imbalanced') : (language === 'es' ? 'Equilibrado' : 'Balanced')} icon={BarChart3} isDark={isDark} color={apcas.bci_index < 0.7 ? "orange" : "green"} />
        <MetricCard title={language === 'es' ? 'NSS (Saturación)' : 'NSS (Saturation)'} value={`${safeNum(apcas.nss_pct, 0)}%`} sub={(() => { const nss = parseFloat(safeNum(apcas.nss_pct, 0)) / 100; if (nss >= 0.95) return language === 'es' ? 'SATURADO' : 'SATURATED'; if (nss >= 0.80) return language === 'es' ? 'CRÍTICO' : 'CRITICAL'; if (nss >= 0.60) return language === 'es' ? 'ELEVADO' : 'ELEVATED'; return language === 'es' ? 'Normal' : 'Normal'; })()} icon={Clock} isDark={isDark} color={(() => { const nss = parseFloat(safeNum(apcas.nss_pct, 0)) / 100; if (nss >= 0.95) return 'red'; if (nss >= 0.80) return 'orange'; if (nss >= 0.60) return 'yellow'; return 'green'; })()} />
        <MetricCard title={language === 'es' ? 'NOP Requerido' : 'NOP Required'} value={`${safeNum(apcas.nop_required, 0)} pos.`} sub={`IPS: ${safeNum(apcas.ips_index ? apcas.ips_index * 100 : 0, 0)}% | AMSRI: ${safeNum(apcas.amsri_index ? apcas.amsri_index * 100 : 0, 0)}%`} icon={Users} isDark={isDark} color="purple" />
        {apcas.cli !== null && (
          <MetricCard
            title="CLI (Controller Load)"
            value={(apcas.cli ?? 0).toFixed(3)}
            sub={(apcas.cli ?? 0) > 1.0 ? (language === 'es' ? 'SOBRECARGA' : 'OVERLOADED') : (apcas.cli ?? 0) > 0.85 ? (language === 'es' ? 'Limite' : 'Near limit') : (language === 'es' ? 'Capacidad disponible' : 'Spare capacity')}
            icon={Activity}
            isDark={isDark}
            color={(apcas.cli ?? 0) > 1.0 ? 'red' : (apcas.cli ?? 0) > 0.85 ? 'orange' : 'green'}
            description={language === 'es' ? 'W/(PHI×T) — Indice de carga del controlador. <1 = capacidad disponible, >1 = sobrecarga' : 'W/(PHI×T) — Controller Load Index. <1 = spare capacity, >1 = overloaded'}
          />
        )}
      </div>

      {/* ROW 2: CAPACITY PIPELINE */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <h3 className={`font-bold text-lg mb-2 ${textMain}`}>{language === 'es' ? 'Pipeline de Capacidad' : 'Capacity Pipeline'}</h3>
        <p className={`text-xs mb-6 ${textSub}`}>{language === 'es' ? 'Cómo se transforma la capacidad teórica en capacidad real' : 'How theoretical capacity transforms into real capacity'}</p>
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
          <div className="flex flex-col items-center p-3 rounded-lg border min-w-[100px] bg-[var(--surface-inset)] border-[var(--border)]">
            <span className={`text-xs font-bold uppercase ${textSub}`}>CMP Infra</span>
            <span className={`text-2xl font-black ${textMain}`}>{safeNum(apcas.cmp_infra, 0)}</span>
            <span className={`text-xs ${textSub}`}>mov/h</span>
          </div>
          <div className="flex flex-col items-center"><ArrowRight size={20} className={textSub} /><span className={`text-xs font-mono ${textSub}`}>×CF {safeNum(apcas.cf_complexity, 3)}</span></div>
          <div className="flex flex-col items-center p-3 rounded-lg border min-w-[100px] bg-[var(--surface-inset)] border-[var(--border)]">
            <span className={`text-xs font-bold uppercase ${textSub}`}>{language === 'es' ? 'Post-Complej.' : 'Post-Complex.'}</span>
            <span className={`text-2xl font-black ${textMain}`}>{safeNum((apcas.cmp_infra || 0) / Math.max(apcas.cf_complexity || 1, 0.01), 0)}</span>
            <span className={`text-xs ${textSub}`}>mov/h</span>
          </div>
          <div className="flex flex-col items-center"><ArrowRight size={20} className={textSub} /><span className={`text-xs font-mono ${textSub}`}>×EF {safeNum(apcas.efficiency_product, 3)}</span></div>
          <div className="flex flex-col items-center p-3 rounded-lg border min-w-[100px] bg-[var(--surface-inset)] border-[var(--border)]">
            <span className={`text-xs font-bold uppercase ${textSub}`}>ACI adj</span>
            <span className={`text-2xl font-black ${textMain}`}>{safeNum(apcas.aci_adj, 1)}</span>
            <span className={`text-xs ${textSub}`}>mov/h</span>
          </div>
          <div className="flex flex-col items-center"><ArrowRight size={20} className={textSub} /><span className={`text-xs font-mono ${textSub}`}>FOC {safeNum(apcas.foc_factor, 2)}</span></div>
          <div
            className="flex flex-col items-center p-4 rounded-lg border-2 min-w-[110px]"
            style={{ background: 'color-mix(in srgb, var(--node-rac) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--node-rac) 45%, transparent)' }}
          >
            <span className="text-xs font-bold uppercase text-[var(--node-rac)]">RAC Final</span>
            <span className="text-3xl font-black text-[var(--node-rac)]">{safeNum(apcas.rac_capacity, 1)}</span>
            <span className={`text-xs ${textSub}`}>mov/h</span>
          </div>
          <div className="flex flex-col items-center"><span className={`text-lg font-bold ${textSub}`}>vs</span></div>
          <div
            className="flex flex-col items-center p-3 rounded-lg border min-w-[100px]"
            style={
              parseFloat(safeNum(apcas.nss_pct, 0)) > 100
                ? { background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 40%, transparent)' }
                : { background: 'var(--surface-inset)', borderColor: 'var(--border)' }
            }
          >
            <span className={`text-xs font-bold uppercase ${textSub}`}>PHT</span>
            <span className={`text-2xl font-black ${parseFloat(safeNum(apcas.nss_pct, 0)) > 100 ? 'text-[var(--aipron-danger)]' : textMain}`}>{safeNum(apcas.pht_horario_realista, 0)}</span>
            <span className={`text-xs ${textSub}`}>{language === 'es' ? 'demanda' : 'demand'}</span>
          </div>
        </div>
      </div>

      {/* ROW 3: Radar + Strategic */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-4 ${textMain}`}>{language === 'es' ? 'Perfil Tri-Nodal' : 'Tri-Node Profile'}</h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={[
                { metric: 'RRC', value: apcas.rrc_input || 30, fullMark: 60 },
                { metric: 'RAC', value: apcas.rac_capacity || 0, fullMark: 60 },
                { metric: 'PTBC', value: apcas.ptbc_input || 17, fullMark: 60 },
                { metric: 'PHT', value: apcas.pht_horario_realista || 0, fullMark: 60 },
                { metric: 'NOP×Cap', value: (apcas.nop_required || 1) * (apcas.capacidad_util_adj || 0), fullMark: 60 },
              ]}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 60]} tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                <Radar name={language === 'es' ? 'Capacidad' : 'Capacity'} dataKey="value" stroke="var(--aipron-accent)" fill="var(--aipron-accent)" fillOpacity={0.3} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div className="p-2 rounded bg-[var(--surface-raised)]">
              <div className={`text-xs uppercase font-bold ${textSub}`}>Runway</div>
              <div className={`text-sm font-bold ${apcas.mismatch_rwy > 0 ? 'text-[var(--aipron-success)]' : 'text-[var(--aipron-danger)]'}`}>{apcas.mismatch_rwy > 0 ? '+' : ''}{safeNum(apcas.mismatch_rwy, 1)}</div>
            </div>
            <div className="p-2 rounded bg-[var(--surface-raised)]">
              <div className={`text-xs uppercase font-bold ${textSub}`}>Apron</div>
              <div className={`text-sm font-bold ${apcas.mismatch_apron > 0 ? 'text-[var(--aipron-success)]' : 'text-[var(--aipron-danger)]'}`}>{apcas.mismatch_apron > 0 ? '+' : ''}{safeNum(apcas.mismatch_apron, 1)}</div>
            </div>
            <div className="p-2 rounded bg-[var(--surface-raised)]">
              <div className={`text-xs uppercase font-bold ${textSub}`}>Terminal</div>
              <div className={`text-sm font-bold ${apcas.mismatch_terminal > 0 ? 'text-[var(--aipron-success)]' : 'text-[var(--aipron-danger)]'}`}>{apcas.mismatch_terminal > 0 ? '+' : ''}{safeNum(apcas.mismatch_terminal, 1)}</div>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${cardBg}`}>
          <h3 className={`font-bold text-sm mb-4 ${textMain}`}>{language === 'es' ? 'Diagnóstico Estratégico' : 'Strategic Diagnosis'}</h3>
          <div className="space-y-3">
            <div
              className="p-3 rounded-lg flex justify-between items-center"
              style={apcas.is_schedule_unsustainable
                ? { background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)' }
                : { background: 'color-mix(in srgb, var(--aipron-success) 10%, transparent)' }}
            >
              <span className={`text-sm ${apcas.is_schedule_unsustainable ? 'text-[var(--aipron-danger)]' : 'text-[var(--aipron-success)]'}`}>{language === 'es' ? 'Horario Sostenible?' : 'Schedule Sustainable?'}</span>
              <span className={`font-bold ${apcas.is_schedule_unsustainable ? 'text-[var(--aipron-danger)]' : 'text-[var(--aipron-success)]'}`}>{apcas.is_schedule_unsustainable ? 'NO' : '✓ YES'}</span>
            </div>
            <div
              className="p-3 rounded-lg flex justify-between items-center"
              style={apcas.is_system_imbalanced
                ? { background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)' }
                : { background: 'color-mix(in srgb, var(--aipron-success) 10%, transparent)' }}
            >
              <span className={`text-sm ${apcas.is_system_imbalanced ? 'text-[var(--aipron-warning)]' : 'text-[var(--aipron-success)]'}`}>System Balanced?</span>
              <span className={`font-bold ${apcas.is_system_imbalanced ? 'text-[var(--aipron-warning)]' : 'text-[var(--aipron-success)]'}`}>{apcas.is_system_imbalanced ? 'IMBALANCED' : '✓ BALANCED'}</span>
            </div>
            <div
              className="p-3 rounded-lg flex justify-between items-center"
              style={apcas.is_apron_the_limiter
                ? { background: 'color-mix(in srgb, var(--node-rac) 12%, transparent)' }
                : { background: 'var(--surface-raised)' }}
            >
              <span className={`text-sm ${apcas.is_apron_the_limiter ? 'text-[var(--node-rac)]' : textSub}`}>Apron is Limiter?</span>
              <span className={`font-bold ${apcas.is_apron_the_limiter ? 'text-[var(--node-rac)]' : textSub}`}>{apcas.is_apron_the_limiter ? 'YES' : 'NO'}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--node-ptbc) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--node-ptbc) 30%, transparent)' }}>
              <div className="text-xs font-bold uppercase text-[var(--node-ptbc)]">AMSRI</div>
              <div className={`text-xl font-bold ${textMain}`}>{safeNum(apcas.amsri_pct || (apcas.amsri_index || 0) * 100, 1)}%</div>
              {apcas.amsri_action && (
                <div
                  className="mt-1 px-2 py-0.5 rounded text-xs font-bold inline-block"
                  style={
                    apcas.amsri_action.includes('IMPLEMENT') ? { background: 'color-mix(in srgb, var(--aipron-danger) 12%, transparent)', color: 'var(--aipron-danger)' }
                    : apcas.amsri_action.includes('EVALUATE') ? { background: 'color-mix(in srgb, var(--aipron-warning) 12%, transparent)', color: 'var(--aipron-warning)' }
                    : { background: 'color-mix(in srgb, var(--aipron-success) 12%, transparent)', color: 'var(--aipron-success)' }
                  }
                >{apcas.amsri_action}</div>
              )}
              {apcas.demand_component !== null && apcas.demand_component !== undefined && (
                <div className={`mt-1 text-xs ${textSub}`}>
                  <span>Demand: {safeNum(apcas.demand_component, 3)}</span>
                  {apcas.tech_gap_component !== null && apcas.tech_gap_component !== undefined && (
                    <span className="ml-2">Tech Gap: {safeNum(apcas.tech_gap_component, 3)}</span>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-danger) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 25%, transparent)' }}>
              <div className="text-xs font-bold uppercase text-[var(--aipron-danger)]">IPS</div>
              <div className={`text-xl font-bold ${textMain}`}>{safeNum(apcas.ips_pct || (apcas.ips_index || 0) * 100, 1)}%</div>
              <div className={`text-xs mt-1 ${textSub}`}>Investment Priority Score</div>
            </div>
          </div>
          <div className="mt-3 p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-info) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}>
            <div className="flex justify-between items-center">
              <div><div className="text-xs font-bold uppercase text-[var(--aipron-info)]">SVS (Slot Viability)</div><div className={`text-xs ${textSub}`}>RGPP: {safeNum(apcas.rgpp, 2)}</div></div>
              <div className={`text-2xl font-bold ${textMain}`}>{safeNum(apcas.svs_pct || (apcas.svs_score || 0) * 100, 1)}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 4: COLLAPSIBLE DETAILS */}
      <div className={`rounded-xl border overflow-hidden ${cardBg}`}>
        <button onClick={() => setShowApcasDetails(!showApcasDetails)} className="w-full p-4 flex justify-between items-center transition-colors hover:bg-[var(--surface-raised)]">
          <div className="flex items-center gap-3">
            <Info size={16} className={textSub} />
            <span className={`text-sm font-bold ${textMain}`}>{language === 'es' ? 'Detalle Bloques 1-7 (Datos Técnicos)' : 'Block 1-7 Detail (Technical Data)'}</span>
          </div>
          {showApcasDetails ? <ChevronUp size={18} className={textSub} /> : <ChevronDown size={18} className={textSub} />}
        </button>
        {showApcasDetails && (
          <div className="p-4 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-info) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 20%, transparent)' }}>
                <h4 className="text-xs font-bold mb-2 text-[var(--aipron-info)]">B1: Baseline</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className={textSub}>PHT:</span><span className={`font-bold ${textMain}`}>{apcas.pht_horario_realista} mov/h</span></div>
                  <div className="flex justify-between"><span className={textSub}>N_dynamic:</span><span className="font-bold text-[var(--aipron-warning)]">{apcas.n_dynamic}</span></div>
                  {apcas.n_dynamic_check !== null && (
                    <div className="flex justify-between"><span className={textSub}>N_dyn (check):</span><span className={`font-bold ${Math.abs((apcas.n_dynamic_check ?? 0) - apcas.n_dynamic) / Math.max(apcas.n_dynamic_check ?? 1, 1e-9) > 0.15 ? 'text-[var(--aipron-warning)]' : textMain}`}>{(apcas.n_dynamic_check ?? 0).toFixed(3)}</span></div>
                  )}
                  {apcas.cli !== null && (
                    <div className="flex justify-between">
                      <span className={textSub}>CLI:</span>
                      <span className={`font-bold ${(apcas.cli ?? 0) > 1.0 ? 'text-[var(--aipron-danger)]' : (apcas.cli ?? 0) > 0.85 ? 'text-[var(--aipron-warning)]' : 'text-[var(--aipron-success)]'}`}>
                        {(apcas.cli ?? 0).toFixed(3)} {(apcas.cli ?? 0) > 1.0 ? '(sobrecarga)' : (apcas.cli ?? 0) > 0.85 ? '(limite)' : '(OK)'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className={textSub}>W_workload:</span><span className="font-bold text-[var(--aipron-warning)]">{apcas.w_workload} min</span></div>
                  <div className="flex justify-between"><span className={textSub}>SC_ctrl:</span><span className={`font-bold ${textMain}`}>{apcas.sc_controller} mov/h/pos</span></div>
                  <div className="flex justify-between"><span className={textSub}>OIF:</span><span className={`font-bold ${textMain}`}>{apcas.oif?.toFixed(3)}</span></div>
                </div>
              </div>
              <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-accent) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-accent) 20%, transparent)' }}>
                <h4 className="text-xs font-bold mb-2 text-[var(--aipron-accent)]">B2: Complexity</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className={textSub}>TCF:</span><span className={`font-bold ${textMain}`}>{apcas.tcf?.toFixed(3)}</span></div>
                  <div className="flex justify-between"><span className={textSub}>WCF:</span><span className={`font-bold ${textMain}`}>{apcas.wcf?.toFixed(3)}</span></div>
                  <div className="flex justify-between"><span className={textSub}>CCF (CNS):</span><span className={`font-bold ${textMain}`}>{apcas.ccf?.toFixed(3)}</span></div>
                  <div className="flex justify-between"><span className={textSub}>CF Total:</span><span className="font-bold text-[var(--aipron-accent)]">{apcas.cf_complexity?.toFixed(3)}</span></div>
                </div>
              </div>
              <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-success) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-success) 20%, transparent)' }}>
                <h4 className="text-xs font-bold mb-2 text-[var(--aipron-success)]">B3-4: Efficiency & Capacity</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className={textSub}>EF:</span><span className={`font-bold ${textMain}`}>{apcas.ef_factor}</span></div>
                  <div className="flex justify-between"><span className={textSub}>A-CDM:</span><span className={`font-bold ${textMain}`}>{apcas.acdm_factor}</span></div>
                  <div className="flex justify-between"><span className={textSub}>N_stands:</span><span className={`font-bold ${textMain}`}>{apcas.n_stands}</span></div>
                  <div className="flex justify-between"><span className={textSub}>FOC:</span><span className={`font-bold ${textMain}`}>{apcas.foc_factor}</span></div>
                  <div className="flex justify-between"><span className={textSub}>RAC:</span><span className="font-bold text-[var(--node-rac)]">{safeNum(apcas.rac_capacity, 1)} mov/h</span></div>
                </div>
              </div>
              <div className="p-3 rounded-lg border" style={{ background: 'color-mix(in srgb, var(--aipron-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 20%, transparent)' }}>
                <h4 className="text-xs font-bold mb-2 text-[var(--aipron-warning)]">B5-7: Saturation & NOP</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className={textSub}>T_max:</span><span className={`font-bold ${textMain}`}>{apcas.t_max} min</span></div>
                  <div className="flex justify-between"><span className={textSub}>NSS%:</span><span className={`font-bold ${textMain}`}>{apcas.nss_pct?.toFixed(1)}%</span></div>
                  <div className="flex justify-between"><span className={textSub}>CTR:</span><span className={`font-bold ${textMain}`}>{apcas.ctr?.toFixed(3)}</span></div>
                  <div className="flex justify-between"><span className={textSub}>Cap/Pos:</span><span className={`font-bold ${textMain}`}>{apcas.capacidad_util_adj?.toFixed(2)} mov/h</span></div>
                  <div className="flex justify-between"><span className={textSub}>NOP:</span><span className="font-bold text-[var(--aipron-warning)]">{apcas.nop_required} pos</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

              {/* Aircraft Mix (ICAO ARC) — the APRON taxonomy */}
              <IcaoArcMixCard
                mix={icaoArcMix}
                cardBg={cardBg}
                textMain={textMain}
                textSub={textSub}
                language={language}
              />

              {/* Capacity Breakdown - Glass Box Visualization */}
              <div className={`p-6 rounded-xl border ${cardBg}`}>
                <div className="flex flex-wrap justify-between items-start mb-4 gap-4">
                  <div>
                    <h3 className={`font-bold text-lg ${textMain}`}>
                      {language === 'es' ? 'Anatomía de la Capacidad' : 'Capacity Anatomy'}
                    </h3>
                    <p className={`text-xs ${textSub} mt-1`}>
                      {language === 'es' ? 'IATA Busy Day (2.º día más ocupado del dataset)' : 'IATA Busy Day (2nd busiest day in dataset)'}
                      {iataBusyDayStr && <span className={`ml-2 font-mono font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{iataBusyDayStr}</span>}
                    </p>
                    {/* Peak badges */}
                    {chartData.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {chartData.length > 8 && (
                          <div className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1.5 ${isDark ? 'bg-amber-900/40 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                            <Sun className="w-3.5 h-3.5" />
                            {language === 'es' ? 'Pico AM' : 'AM Peak'}: 08:00 → {(apcas.rac_capacity || 25).toFixed(1)} mov/h
                          </div>
                        )}
                        {chartData.length > 18 && (
                          <div className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1.5 ${isDark ? 'bg-orange-900/40 text-orange-300 border border-orange-500/20' : 'bg-orange-50 text-orange-700 border border-orange-200'}`}>
                            <Sunset className="w-3.5 h-3.5" />
                            {language === 'es' ? 'Pico PM' : 'PM Peak'}: 18:00 → {(apcas.rac_capacity || 25).toFixed(1)} mov/h
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`flex flex-wrap gap-3 text-xs font-bold uppercase ${textMain}`}>
                    {/* Demanda */}
                    <div className="relative group cursor-help">
                      <div className="flex items-center gap-2">
                        <svg width="20" height="10"><line x1="0" y1="5" x2="20" y2="5" stroke="#f97316" strokeWidth="2.5"/><circle cx="10" cy="5" r="3" fill="#f97316"/></svg>
                        {language === 'es' ? 'Demanda' : 'Demand'}
                      </div>
                      <div className="absolute z-50 hidden group-hover:block top-full right-0 mt-2 w-72 p-3 rounded-lg shadow-2xl border border-orange-500/30 bg-[var(--surface-raised)] text-xs font-normal normal-case leading-relaxed">
                        <p className="font-bold text-orange-400 mb-1">{language === 'es' ? 'Demanda (mov/h)' : 'Demand (mov/h)'}</p>
                        <p className="text-[var(--text-secondary)]">
                          {language === 'es'
                            ? 'Número de movimientos reales (aterrizajes + despegues) por hora registrados en el AODB. Es la carga de trabajo real que la plataforma debe absorber. Cuando la demanda supera la RAC, se producen retrasos y congestión.'
                            : 'Actual movements (arrivals + departures) per hour from the AODB. This is the real workload the apron must absorb. When demand exceeds RAC, delays and congestion occur.'}
                        </p>
                      </div>
                    </div>
                    {/* RAC */}
                    <div className="relative group cursor-help">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-cyan-400"></div> RAC</div>
                      <div className="absolute z-50 hidden group-hover:block top-full right-0 mt-2 w-72 p-3 rounded-lg shadow-2xl border border-cyan-500/30 bg-[var(--surface-raised)] text-xs font-normal normal-case leading-relaxed">
                        <p className="font-bold text-cyan-400 mb-1">{language === 'es' ? 'RAC — Capacidad Real de Plataforma' : 'RAC — Real Apron Capacity'}</p>
                        <p className="text-[var(--text-secondary)]">
                          {language === 'es'
                            ? 'Capacidad efectiva de la plataforma tras aplicar todas las penalizaciones operacionales (dotación, complejidad, intensidad). Es el número máximo de movimientos que la plataforma puede gestionar de forma segura y eficiente en una hora.'
                            : 'Effective apron capacity after all operational penalties (staffing, complexity, intensity). The maximum movements the apron can safely and efficiently handle per hour.'}
                        </p>
                      </div>
                    </div>
                    {/* Dotación */}
                    <div className="relative group cursor-help">
                      <div className="flex items-center gap-2">
                        <svg width="20" height="10"><line x1="0" y1="5" x2="20" y2="5" stroke="#f87171" strokeWidth="2" strokeDasharray="5 2"/></svg>
                        {language === 'es' ? 'Dotación' : 'Staffing'}
                      </div>
                      <div className="absolute z-50 hidden group-hover:block top-full right-0 mt-2 w-72 p-3 rounded-lg shadow-2xl border border-red-500/30 bg-[var(--surface-raised)] text-xs font-normal normal-case leading-relaxed">
                        <p className="font-bold text-red-400 mb-1">{language === 'es' ? 'Pérdida por Dotación' : 'Staffing Loss'}</p>
                        <p className="text-[var(--text-secondary)]">
                          {language === 'es'
                            ? 'Capacidad perdida por insuficiente personal operativo (controladores, señaleros, coordinadores). Cuando el NOP real es inferior al necesario, se reduce la capacidad de gestión de tráfico. Es el factor más crítico y el más costoso de corregir.'
                            : 'Capacity lost due to insufficient operational staff (controllers, marshals, coordinators). When actual NOP is below required, traffic handling capacity decreases. This is the most critical and costly factor to correct.'}
                        </p>
                      </div>
                    </div>
                    {/* Complejidad */}
                    <div className="relative group cursor-help">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-amber-500"></div> {language === 'es' ? 'Complejidad' : 'Complexity'}</div>
                      <div className="absolute z-50 hidden group-hover:block top-full right-0 mt-2 w-72 p-3 rounded-lg shadow-2xl border border-amber-500/30 bg-[var(--surface-raised)] text-xs font-normal normal-case leading-relaxed">
                        <p className="font-bold text-amber-400 mb-1">{language === 'es' ? 'Pérdida por Complejidad (CF)' : 'Complexity Loss (CF)'}</p>
                        <p className="text-[var(--text-secondary)]">
                          {language === 'es'
                            ? 'Capacidad perdida por factores de complejidad operacional: meteorología adversa (WCF), operaciones nocturnas (NOF), obras/interferencias en pista (WIF) y complejidad de calles de rodaje (TCF). Se incrementa en condiciones meteorológicas desfavorables o con restricciones de infraestructura.'
                            : 'Capacity lost due to operational complexity factors: adverse weather (WCF), night operations (NOF), runway works/interference (WIF), and taxiway complexity (TCF). Increases in unfavorable weather or with infrastructure restrictions.'}
                        </p>
                      </div>
                    </div>
                    {/* Intensidad */}
                    <div className="relative group cursor-help">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-purple-500"></div> {language === 'es' ? 'Intensidad' : 'Intensity'}</div>
                      <div className="absolute z-50 hidden group-hover:block top-full right-0 mt-2 w-72 p-3 rounded-lg shadow-2xl border border-purple-500/30 bg-[var(--surface-raised)] text-xs font-normal normal-case leading-relaxed">
                        <p className="font-bold text-purple-400 mb-1">{language === 'es' ? 'Pérdida por Intensidad (OIF)' : 'Intensity Loss (OIF)'}</p>
                        <p className="text-[var(--text-secondary)]">
                          {language === 'es'
                            ? 'Capacidad perdida cuando la Intensidad Operacional (OIF) es elevada. Refleja la presión acumulada por alta carga de trabajo del controlador, mezcla de tráfico compleja y tiempos de sector reducidos. A mayor OIF, menor margen de maniobra para el personal.'
                            : 'Capacity lost when Operational Intensity (OIF) is high. Reflects cumulative pressure from high controller workload, complex traffic mix, and reduced sector times. Higher OIF means less operational margin for staff.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-[350px] w-full">
                  <RACStratigraphy
                    hourlyData={(() => {
                      const baseRac = apcas.rac_capacity || 25;
                      const cmpInfraV = apcas.cmp_infra || 35;
                      const cfV = Math.max(apcas.cf_complexity || 1.0, 1.0);
                      const oifV = Math.max(apcas.oif || 1.0, 1.0);
                      // Distribute total capacity loss proportionally between CF and OIF
                      const totalLoss = Math.max(cmpInfraV - baseRac, 0);
                      const cfExcess = Math.max(cfV - 1.0, 0);
                      const oifExcess = Math.max(oifV - 1.0, 0);
                      const totalExcess = cfExcess + oifExcess;
                      const lossComplexity = totalExcess > 0 ? totalLoss * (cfExcess / totalExcess) : 0;
                      const lossIntensity = totalExcess > 0 ? totalLoss * (oifExcess / totalExcess) : 0;
                      const lossStaffing = Math.max(totalLoss - lossComplexity - lossIntensity, 0);
                      return chartData.map((d: any, i: number) => {
                        const nopRaw = nopHourly[i];
                        const nopCapacity = capacidadUtilAdj > 0 && nopRaw != null
                          ? Math.min(nopRaw * capacidadUtilAdj, cmpInfraV)
                          : undefined;
                        return {
                          hour: i,
                          rac: baseRac,
                          demand: d.demand || 0,
                          nop: nopCapacity,
                          lossStaffing,
                          lossComplexity,
                          lossIntensity,
                          cmpInfra: cmpInfraV,
                        };
                      });
                    })()}
                    isDark={isDark}
                    language={language as 'es' | 'en'}
                  />
                </div>
                <p className={`text-xs mt-4 ${textSub}`}>
                  {language === 'es'
                    ? 'Esta visualización muestra cómo se "pierde" capacidad respecto al máximo teórico (CMP Infra) debido a factores de dotación, complejidad e intensidad operacional.'
                    : 'This visualization shows how capacity is "lost" relative to theoretical maximum (CMP Infra) due to staffing, complexity, and operational intensity factors.'}
                </p>
              </div>

              {/* Busy-day traffic waves ("banks") — relocated from the Overview:
                  this is analysis, not an As-Is fact, and it is a statistical wave
                  detector, not the airport's scheduled banks (see its info hint). */}
              <BankAnalysis bankData={bankData} bankChartData={bankChartData} language={language as 'es' | 'en'} />

              {/* CAPAN declared-capacity regression (glass-box, task A). Dormant until
                  D-FOL occupancy samples flow into the pipeline (#24 dfol_ingest). */}
              <div className="mt-4">
                <CapacityRegressionPanel
                  data={(apcas as any)?.capacity_regression}
                  scController={apcas?.sc_controller}
                  rac={apcas?.rac_capacity}
                  isDark={isDark}
                />
              </div>
    </div>
  );
};

/** ICAO ARC (Annex 14) aircraft mix of the apron — the taxonomy that sizes stands
 *  and taxiways, by wingspan / outer main gear span. Deliberately labelled so it is
 *  never confused with the runway node's RECAT-EU mix: the letters A-F are the same
 *  and mean different things (an A320 is ARC C but RECAT D).
 *  Source: analysis_results.capacity_integration.icao_arc_mix, classified with
 *  apcas_logic.classify_aircraft_arc — the same DB as AP-CAM Block 1. */
const ARC_LABELS: Record<string, string> = {
  arc_a: 'A — < 15 m',
  arc_b: 'B — 15-24 m',
  arc_c: 'C — 24-36 m',
  arc_d: 'D — 36-52 m',
  arc_e: 'E — 52-65 m',
  arc_f: 'F — 65-80 m',
};

const IcaoArcMixCard = ({
  mix,
  cardBg,
  textMain,
  textSub,
  language,
}: {
  mix: Record<string, number> | null;
  cardBg: string;
  textMain: string;
  textSub: string;
  language: string;
}) => {
  const es = language === 'es';
  return (
    <div className={`p-6 rounded-xl border ${cardBg}`}>
      <div className="flex flex-wrap justify-between items-start gap-3 mb-1">
        <h3 className={`font-bold text-lg ${textMain}`}>
          {es ? 'Mezcla de flota (ICAO ARC — envergadura)' : 'Aircraft Mix (ICAO ARC — wingspan)'}
        </h3>
        <span className="text-[10px] px-2 py-0.5 rounded-sm font-bold tracking-wide bg-[var(--node-rac)]/20 text-[var(--node-rac)]">
          {es ? 'PLATAFORMA' : 'APRON'}
        </span>
      </div>
      <p className={`text-xs ${textSub} mb-4`}>
        {es
          ? 'Código de referencia de aeródromo (Anexo 14): dimensiona puestos y calles de rodaje. Un A320 es C. El nodo PISTA usa RECAT-EU, otra taxonomía con las mismas letras — ahí el mismo A320 es D.'
          : 'Aerodrome reference code (Annex 14): sizes stands and taxiways. An A320 is C. The RUNWAY node uses RECAT-EU, a different taxonomy sharing the same letters — there the same A320 is D.'}
      </p>
      {!mix ? (
        <div className={`text-center py-4 text-xs ${textSub}`}>
          {es ? 'Sin dato de mezcla ICAO ARC' : 'No ICAO ARC mix data'}
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(ARC_LABELS).map(([key, label]) => {
            const pct = mix[key] ?? 0;
            return (
              <div key={key} className="flex items-center justify-between gap-3">
                <span className={`text-xs ${textSub} w-24 shrink-0`}>{label}</span>
                <div className="flex-1 h-2 rounded-full bg-[var(--border)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[var(--node-rac)]"
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <span className={`text-xs font-mono w-14 text-right ${textMain}`}>
                  {pct.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApcasPage;
